import { useState, useCallback, useEffect } from 'react';
import {
  getMyLikeStatus,
  getPublicLikeStatus,
  toggleLike as toggleLikeApi,
  type InteractionTargetType,
} from '@/api/interaction';
import { useAuth } from '@/hooks/useAuth';

interface LikeState {
  likeCount: number;
  isLiked: boolean;
  rewardCount: number;
  canEarnReward: boolean;
  maxRewardLikes: number;
  featureEnabled: boolean;
}

const DEFAULT_STATE: LikeState = {
  likeCount: 0,
  isLiked: false,
  rewardCount: 0,
  canEarnReward: false,
  maxRewardLikes: 0,
  featureEnabled: false,
};

function isValidTargetId(id: string) {
  const n = Number(id);
  return Number.isInteger(n) && n > 0;
}

export function useLike(type: InteractionTargetType, id: string, title: string) {
  const { user, isLoggedIn } = useAuth();
  const [state, setState] = useState<LikeState>(DEFAULT_STATE);

  useEffect(() => {
    let cancelled = false;

    async function syncStatus() {
      if (!isValidTargetId(id)) {
        if (!cancelled) setState(DEFAULT_STATE);
        return;
      }
      const targetId = Number(id);
      try {
        if (isLoggedIn && user?.id) {
          const res = await getMyLikeStatus({ targetType: type, targetId });
          if (cancelled) return;
          setState({
            likeCount: res.likeCount,
            isLiked: res.isLiked,
            rewardCount: res.rewardCount,
            canEarnReward: res.canEarnReward,
            maxRewardLikes: res.maxRewardLikes,
            featureEnabled: res.featureEnabled,
          });
          return;
        }

        const res = await getPublicLikeStatus({ targetType: type, targetId });
        if (cancelled) return;
        setState({
          likeCount: res.likeCount,
          isLiked: false,
          rewardCount: 0,
          canEarnReward: false,
          maxRewardLikes: 0,
          featureEnabled: res.featureEnabled,
        });
      } catch (err) {
        console.error('Failed to sync like status:', err);
        if (cancelled) return;
        setState(DEFAULT_STATE);
      }
    }

    void syncStatus();
    return () => {
      cancelled = true;
    };
  }, [id, isLoggedIn, type, user?.id]);

  const toggle = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      return { success: false, message: '请先登录', rewarded: false, isLiked: state.isLiked };
    }
    if (!isValidTargetId(id)) {
      return { success: false, message: '目标 ID 不合法', rewarded: false, isLiked: state.isLiked };
    }
    const targetId = Number(id);

    try {
      const res = await toggleLikeApi({
        targetType: type,
        targetId,
        targetTitle: title,
      });

      setState({
        likeCount: res.likeCount,
        isLiked: res.isLiked,
        rewardCount: res.rewardCount,
        canEarnReward: res.canEarnReward,
        maxRewardLikes: res.maxRewardLikes,
        featureEnabled: res.featureEnabled,
      });

      return {
        success: true,
        message: res.message,
        rewarded: res.rewarded,
        isLiked: res.isLiked,
      };
    } catch (err) {
      console.error('Failed to toggle like:', err);
      return {
        success: false,
        message: err instanceof Error ? err.message : '点赞失败，请稍后重试',
        rewarded: false,
        isLiked: state.isLiked,
      };
    }
  }, [id, isLoggedIn, state.isLiked, title, type, user?.id]);

  return {
    likeCount: state.likeCount,
    isLiked: state.isLiked,
    canEarnReward: state.canEarnReward,
    rewardCount: state.rewardCount,
    maxRewardLikes: state.maxRewardLikes,
    toggle,
    featureEnabled: state.featureEnabled,
  };
}
